import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';
import { useAuth } from '@/lib/auth';
import { useStaffV2Flags } from '@/lib/staffV2Flags';
import { TaskRow } from '@/pages/staff/MyTasksPage';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, AlertTriangle, CheckCircle2, Plus, FileText, Car } from 'lucide-react';
import { CarePlanDetail } from '@/components/participants/care-plan/CarePlanDetail';
import { ParticipantChartCreateModal } from '@/pages/participants/charts/ParticipantChartCreateModal';   // BS3-011
import { BowelChartEntryFormModal } from '@/pages/participants/charts/BowelChartEntryFormModal';
import { SleepChartEntryFormModal } from '@/pages/participants/charts/SleepChartEntryFormModal';
import { ChartDynamicEntryForm } from '@/pages/participants/charts/ChartDynamicEntryForm';
import { getTemplate as getChartTemplate } from '@/pages/participants/charts/templatesApi';
import { GoalReadOnlyDetail } from '@/components/participants/goals/GoalReadOnlyDetail';
import { ProgressNoteForm, emptyNoteForm } from '@/components/participants/notes/ProgressNoteForm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MedicationChartNotice from '@/components/shared/MedicationChartNotice';
import { noteShiftOptions, defaultNoteShift } from '@/lib/staffNoteShifts';
import { roleLabel } from '@/lib/roleLabels';

/* Staff-scoped participant profile — approved grouped IA. Read-only reuse of
   existing modules; Progress Notes expose the Phase 1 amend/strike workflow. */

const GROUPS = [
  { title: 'Care Planning', tabs: [['careplans', 'Care Plans'], ['goals', 'Goals']] },
  { title: 'Clinical', tabs: [['charts', 'Clinical Charts'], ['medication', 'Medication'], ['risk', 'Risk Assessments'], ['bsp', 'Behaviour Support Plans']] },
  { title: 'Notes & Records', tabs: [['notes', 'Progress Notes'], ['handover', 'Handover Notes'], ['documents', 'Documents']] },
  { title: 'Scheduling & Safety', tabs: [['appointments', 'Appointments'], ['incidents', 'Incident Register'], ['vehicle', 'Available Vehicle']] },
];

function useFetch(url, params) {
  const [state, setState] = useState({ data: null, error: null, denied: false, loading: true });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(url, { params });
      setState({ data: r.data.data ?? r.data, error: null, denied: false, loading: false });
    } catch (e) {
      const st = e?.response?.status;
      setState({ data: null, denied: st === 403, error: st === 403 ? null : (e?.response?.data?.error?.message || 'Failed to load'), loading: false });
    }
    // eslint-disable-next-line
  }, [url, JSON.stringify(params)]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function TabShell({ loading, error, denied, empty, emptyLabel, onRetry, children, testId }) {
  if (loading) return <div className="py-8 text-center text-sm text-muted-foreground" aria-busy="true" data-testid={`${testId}-loading`}>Loading…</div>;
  if (denied) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid={`${testId}-denied`}>You don&apos;t have permission to view this section.</div>;
  if (error) return (
    <div className="py-8 text-center space-y-2" data-testid={`${testId}-error`}>
      <p className="text-sm text-muted-foreground">{error}</p>
      {onRetry && <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>}
    </div>
  );
  if (empty) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{emptyLabel}</div>;
  return children;
}

function Row({ title, meta, badge, testId, struck }) {
  return (
    <div className="py-2.5 border-b last:border-0 flex items-start gap-2" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${struck ? 'line-through text-muted-foreground' : ''}`}>{title}</div>
        {meta && <div className="text-xs text-muted-foreground mt-0.5">{meta}</div>}
      </div>
      {badge}
    </div>
  );
}

function ListTab({ url, params, mapRow, emptyLabel, testId, filterItems }) {
  const { data, error, denied, loading, reload } = useFetch(url, params);
  let items = Array.isArray(data) ? data : (data?.items || data?.data || []);
  if (filterItems) items = items.filter(filterItems);
  return (
    <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel={emptyLabel} onRetry={reload} testId={testId}>
      <div data-testid={testId}>{items.slice(0, 25).map(mapRow)}</div>
    </TabShell>
  );
}

/* ── Care Plans: read-only list + detail viewer (reuses CarePlanDetail) ── */
export const STAFF_VISIBLE_CAREPLAN_STATUSES = ['APPROVED', 'ACTIVE'];   // BS3-012
export function CarePlansTab({ pid }) {
  const { data, error, denied, loading, reload } = useFetch(`/careplans/by-participant/${pid}`);
  const all = Array.isArray(data) ? data : (data?.items || data?.data || []);
  // BS3-012: staff see APPROVED plans only — the same rule as the mobile brief and the shift acknowledgement
  // (a draft or a plan still in review is not shown; the admin is told this when creating it).
  const items = all.filter((c) => STAFF_VISIBLE_CAREPLAN_STATUSES.includes((c.status || '').toUpperCase()));
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const openPlan = async (id) => {
    setOpenId(id); setDetail(null); setDetailError(null); setDetailLoading(true);
    try {
      const r = await api.get(`/careplans/${id}`);
      setDetail(r.data.data ?? r.data);
    } catch (e) {
      setDetailError(e?.response?.data?.error?.message || 'Unable to load care plan');
    } finally { setDetailLoading(false); }
  };
  const close = () => { setOpenId(null); setDetail(null); setDetailError(null); };

  return (
    <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel="No approved care plan yet." onRetry={reload} testId="tab-careplans">
      <div data-testid="tab-careplans">
        {items.slice(0, 25).map((c) => (
          <div key={c.id} className="py-2.5 border-b last:border-0 flex items-center gap-2" data-testid={`careplan-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{c.title || c.plan_name || 'Care plan'}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Status {c.status || '—'} {c.review_date ? `· review ${c.review_date}` : ''}</div>
            </div>
            <Badge variant="outline">{c.status || '—'}</Badge>
            <Button size="sm" variant="outline" className="h-7" onClick={() => openPlan(c.id)} data-testid={`careplan-view-${c.id}`}>View</Button>
          </div>
        ))}
      </div>
      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="careplan-detail-dialog">
          <DialogHeader><DialogTitle>Care Plan</DialogTitle></DialogHeader>
          {detailLoading && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="careplan-detail-loading">Loading…</div>}
          {!detailLoading && detailError && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="careplan-detail-error">{detailError}</div>}
          {!detailLoading && !detailError && detail && (
            <CarePlanDetail
              plan={detail}
              linkedGoals={detail.linked_goals}
              linkedRisks={detail.linked_risks}
              linkedRestrictivePractices={detail.linked_restrictive_practices}
              linkedMedications={detail.linked_medications}
              acknowledgements={detail.acknowledgements}
              readOnly
            />
          )}
        </DialogContent>
      </Dialog>
    </TabShell>
  );
}

/* ── Goals: read-only list + detail viewer (goals.view only) ── */
export function GoalsTab({ pid }) {
  const { data, error, denied, loading, reload } = useFetch(`/goals/by-participant/${pid}`);
  const items = Array.isArray(data) ? data : (data?.items || data?.data || []);
  const [openId, setOpenId] = useState(null);
  const [goal, setGoal] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineError, setTimelineError] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const openGoal = async (id) => {
    setOpenId(id); setGoal(null); setTimeline(null); setTimelineError(false); setDetailError(null); setDetailLoading(true);
    try {
      const r = await api.get(`/goals/${id}`);
      setGoal(r.data.data ?? r.data);
    } catch (e) {
      setDetailError(e?.response?.data?.error?.message || 'Unable to load goal');
      setDetailLoading(false);
      return;
    }
    setDetailLoading(false);
    // Timeline is best-effort (404 when SMART framework off) — never blocks the goal.
    try {
      const t = await api.get(`/goals/${id}/timeline`);
      setTimeline(t.data.data ?? t.data);
    } catch (e) {
      setTimelineError(true);
    }
  };
  const close = () => { setOpenId(null); setGoal(null); setTimeline(null); setTimelineError(false); setDetailError(null); };

  return (
    <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel="No goals recorded." onRetry={reload} testId="tab-goals">
      <div data-testid="tab-goals">
        {items.slice(0, 25).map((g) => (
          <div key={g.id} className="py-2.5 border-b last:border-0 flex items-center gap-2" data-testid={`goal-${g.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{g.title || g.goal}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {g.progress != null ? `Progress ${g.progress}% ` : ''}{g.target_date ? `· target ${g.target_date} ` : ''}{g.updated_at ? `· updated ${new Date(g.updated_at).toLocaleDateString()}` : ''}
              </div>
            </div>
            <Badge variant="outline">{g.status || '—'}</Badge>
            <Button size="sm" variant="outline" className="h-7" onClick={() => openGoal(g.id)} data-testid={`goal-view-${g.id}`}>View</Button>
          </div>
        ))}
      </div>
      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="goal-detail-dialog">
          <DialogHeader><DialogTitle>Goal</DialogTitle></DialogHeader>
          {detailLoading && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="goal-detail-loading">Loading…</div>}
          {!detailLoading && detailError && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="goal-detail-error">{detailError}</div>}
          {!detailLoading && !detailError && goal && (
            <GoalReadOnlyDetail goal={goal} timeline={timeline} timelineError={timelineError} />
          )}
        </DialogContent>
      </Dialog>
    </TabShell>
  );
}

/* ── Progress Notes with Phase 1 amend / strike ── */
export function NotesTab({ pid }) {
  const { hasPermission } = useAuth();
  const { data, error, denied, loading, reload } = useFetch(`/notes/by-participant/${pid}`);
  const [dialog, setDialog] = useState(null); // {mode:'create', note}
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [noteForm, setNoteForm] = useState(emptyNoteForm());
  const [eligibleShifts, setEligibleShifts] = useState([]);
  const [shiftId, setShiftId] = useState('');
  const items = Array.isArray(data) ? data : (data?.items || []);

  const openCreate = async () => {
    setNoteForm(emptyNoteForm()); setShiftId(''); setEligibleShifts([]);
    setDialog({ mode: 'create' });
    try {
      const r = await api.get('/roster/my-shifts', { params: { pageSize: 100 } });
      // CR27-21: any of the worker's shifts with this participant — also after clock-out (notes are often written at home)
      const mine = noteShiftOptions(r.data.items, pid);
      setEligibleShifts(mine);
      setShiftId(defaultNoteShift(mine)?.id || '');
    } catch (e) { /* selector will show none */ }
  };

  const createNote = async (submitAfter) => {
    if (!shiftId) { toast.error('Select an eligible shift for this participant'); return; }
    if (!noteForm.note.trim()) { toast.error('Note is required'); return; }
    setBusy(true);
    try {
      const res = await api.post('/notes', { shift_id: shiftId, participant_id: pid, ...noteForm });
      if (submitAfter) {
        await api.post(`/notes/${res.data.data.id}/submit`);
        toast.success('Note submitted');
      } else {
        toast.success('Note saved as draft');
      }
      setDialog(null); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.error?.message || e?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {hasPermission('notes.create') && (
        <Button size="sm" onClick={openCreate} data-testid="note-create-btn">
          <Plus className="h-4 w-4 mr-1.5" />New Note
        </Button>
      )}
      <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel="No progress notes yet." onRetry={reload} testId="tab-notes">
        <div>
          {items.map((n) => (
            <div key={n.id} className="py-3 border-b last:border-0" data-testid={`note-${n.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className={`text-sm whitespace-pre-wrap ${n.struck ? 'line-through text-muted-foreground' : ''}`} data-testid={`note-text-${n.id}`}>{n.note}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {n.author_name || 'Staff'} · {n.created_at ? new Date(n.created_at).toLocaleString() : ''} · {n.status}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {n.struck && <Badge variant="destructive" className="text-[10px]" data-testid={`note-struck-${n.id}`}>Struck</Badge>}
                  {(n.amendments?.length > 0) && (
                    <button className="text-[11px] underline text-muted-foreground" onClick={() => setExpanded(expanded === n.id ? null : n.id)} data-testid={`note-history-${n.id}`}>
                      {n.amendments.length} amendment{n.amendments.length > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
              {expanded === n.id && (
                <div className="mt-2 pl-3 border-l-2 border-border space-y-2" data-testid={`note-history-panel-${n.id}`}>
                  {n.original_note && <div className="text-xs"><span className="font-medium">Original:</span> <span className="text-muted-foreground">{n.original_note}</span></div>}
                  {(n.amendments || []).map((a) => (
                    <div key={a.seq} className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">Amendment {a.seq}</span> by {a.actor_name} · {new Date(a.amended_at).toLocaleString()} — reason: {a.reason}
                    </div>
                  ))}
                  {n.struck && <div className="text-xs text-red-600">Struck by {n.struck.actor_name} · {new Date(n.struck.struck_at).toLocaleString()} — reason: {n.struck.reason}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </TabShell>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="note-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>
            {dialog?.mode === 'create' ? 'New progress note' : dialog?.mode === 'amend' ? 'Amend note (original is preserved)' : 'Strike through note'}
          </DialogTitle></DialogHeader>

          {dialog?.mode === 'create' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Shift *</label>
                {eligibleShifts.length === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="note-no-shift">You have no shifts with this participant to add a progress note to.</p>
                ) : (
                  <Select value={shiftId} onValueChange={setShiftId}>
                    <SelectTrigger className="h-9 text-sm" data-testid="note-shift-select"><SelectValue placeholder="Select shift" /></SelectTrigger>
                    <SelectContent>
                      {eligibleShifts.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {`${s.date || ''} ${s.start_time ? `· ${s.start_time}${s.end_time ? `–${s.end_time}` : ''}` : ''} ${s.service_type ? `· ${s.service_type}` : ''} ${s.status ? `· ${s.status}` : ''}`.trim()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <ProgressNoteForm form={noteForm} onChange={setNoteForm} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
                <Button variant="outline" onClick={() => createNote(false)} disabled={busy || !shiftId || !noteForm.note.trim()} data-testid="note-save-draft">Save Draft</Button>
                <Button onClick={() => createNote(true)} disabled={busy || !shiftId || !noteForm.note.trim()} data-testid="note-save-submit">{busy ? 'Saving…' : 'Save & Submit'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HandoverTab({ pid }) {
  const { hasPermission } = useAuth();
  const { data, error, denied, loading, reload } = useFetch(`/handover/by-participant/${pid}`);
  const items = Array.isArray(data) ? data : [];
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const ack = async (id) => {
    try {
      await api.post(`/handover/${id}/acknowledge`, {});
      toast.success('Handover acknowledged');
      window.dispatchEvent(new Event('staff-v2-handover-ack'));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || 'Unable to acknowledge'); }
  };
  const create = async () => {
    if (!summary.trim()) { toast.error('A handover summary is required'); return; }
    setBusy(true);
    try {
      const shifts = await api.get('/roster/my-shifts', { params: { pageSize: 100 } });
      // CR27-24: the shift in progress, else the latest started one — not whichever shift the list returned first
      const shift = defaultNoteShift(noteShiftOptions(shifts.data.items, pid));
      if (!shift) { toast.error('You need a shift with this participant before adding a handover'); return; }
      await api.post('/handover', { shift_id: shift.id, participant_id: pid, handover_summary: summary });
      toast.success('Handover created');
      setOpen(false); setSummary(''); reload();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.error?.message || 'Unable to create handover'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      {hasPermission('handover.create') && (
        <Button size="sm" onClick={() => { setSummary(''); setOpen(true); }} data-testid="handover-create-btn">
          <Plus className="h-4 w-4 mr-1.5" />New Handover
        </Button>
      )}
      <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel="No handover notes for this participant." onRetry={reload} testId="tab-handover">
        <div>
          {items.map((h) => (
            <div key={h.id} className="py-3 border-b last:border-0" data-testid={`handover-${h.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</div>
                {h.acknowledged_by_me
                  ? <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" />Acknowledged</Badge>
                  : <Button size="sm" className="h-7" onClick={() => ack(h.id)} data-testid={`handover-ack-${h.id}`}>Acknowledge</Button>}
              </div>
              <p className="text-sm mt-1">{h.handover_summary}</p>
              {(h.acknowledgements || []).length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">Acknowledged by {h.acknowledgements.map((a) => a.staff_name || 'staff').join(', ')}</p>
              )}
            </div>
          ))}
        </div>
      </TabShell>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="handover-dialog">
          <DialogHeader><DialogTitle>New handover note</DialogTitle></DialogHeader>
          <Textarea rows={5} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Handover summary" data-testid="handover-summary-input" aria-label="Handover summary" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !summary.trim()} data-testid="handover-dialog-submit">{busy ? 'Saving…' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// CR27-36: the latest recorded dose (by the admin or any worker), e.g. "Last: Given · 30/10/2026, 2:10 pm · Jane Smith"
const lastDoseText = (a) => ['Last: ' + ((a.status || '').toUpperCase() === 'ADMINISTERED' ? 'Given' : String(a.status || '').charAt(0) + String(a.status || '').slice(1).toLowerCase()),
  a.at ? new Date(a.at).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null, a.by].filter(Boolean).join(' · ');

export function MedicationTab({ pid }) {
  const { hasPermission } = useAuth();
  const { data, error, denied, loading, reload } = useFetch(`/medications/by-participant/${pid}`);
  const items = Array.isArray(data) ? data : (data?.items || data?.data || []);
  const [dialog, setDialog] = useState(null); // medication being administered
  const [form, setForm] = useState({ scheduled_time: '', dose_given: '', notes: '', outcome: 'administer' });
  const [busy, setBusy] = useState(false);
  // eMAR administration follows the SAME contract as the backend
  // (require_permission('emar.administer')). There is no `emar_enabled`
  // feature flag in the backend or the flags API — the old flag gate was a
  // phantom dependency that permanently hid the action.
  const canAdminister = typeof hasPermission === 'function' && hasPermission('emar.administer');
  // Convenience default: current local time as zero-padded 24h HH:MM, computed
  // fresh each time the dialog opens. Stays editable; the actual administration
  // timestamp (administered_at) is server-generated and unaffected.
  const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const openAdminister = (m) => {
    setForm({ scheduled_time: nowHHMM(), dose_given: '', notes: '', outcome: 'administer' });
    setDialog(m);
  };
  const submit = async () => {
    if (!form.scheduled_time.trim()) { toast.error('Scheduled time (HH:MM) is required'); return; }
    setBusy(true);
    try {
      const shifts = await api.get('/roster/my-shifts', { params: { pageSize: 100 } });
      const mine = (shifts.data.items || []).filter((s) => s.participant_id === pid);
      if (!mine.length) { toast.error('You need a shift with this participant to record administration'); return; }
      const path = form.outcome === 'refuse' ? '/emar/refuse' : form.outcome === 'withhold' ? '/emar/withhold' : '/emar/administer';
      await api.post(path, {
        participant_id: pid, medication_id: dialog.id, shift_id: mine[0].id,
        scheduled_time: form.scheduled_time, dose_given: form.dose_given || undefined, notes: form.notes || undefined,
      });
      toast.success('Medication administration recorded');
      setDialog(null); setForm({ scheduled_time: '', dose_given: '', notes: '', outcome: 'administer' }); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.error?.message || 'Unable to record administration');
    } finally { setBusy(false); }
  };
  return (
    <>
    <div className="mb-3"><MedicationChartNotice /></div>
    <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel="No medications recorded." onRetry={reload} testId="tab-medication">
      <div data-testid="tab-medication">
        {!canAdminister && (
          <p className="text-xs text-muted-foreground mb-2" data-testid="med-view-only-hint">
            View only — you don't have permission to record medication administration.
          </p>
        )}
        {items.slice(0, 25).map((m) => (
          <div key={m.id} className="py-2.5 border-b last:border-0 flex items-center gap-2" data-testid={`med-${m.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{m.name || m.medication_name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{m.dosage || ''} {m.frequency ? `· ${m.frequency}` : ''}</div>
              {m.last_administration && (
                <div className="text-xs mt-0.5" data-testid={`med-last-${m.id}`}>{lastDoseText(m.last_administration)}</div>
              )}
            </div>
            <Badge variant="outline">{m.status || '—'}</Badge>
            {canAdminister && (
              <Button size="sm" variant="outline" className="h-7" onClick={() => openAdminister(m)} data-testid={`med-administer-${m.id}`}>Administer</Button>
            )}
          </div>
        ))}
      </div>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="med-administer-dialog">
          <DialogHeader><DialogTitle>Record administration — {dialog?.name || dialog?.medication_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="text-xs text-muted-foreground block">Outcome
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="med-outcome" aria-label="Outcome">
                <option value="administer">Given</option>
                <option value="refuse">Refused</option>
                <option value="withhold">Withheld</option>
              </select>
            </label>
            <Input placeholder="Scheduled time (HH:MM) *" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} data-testid="med-scheduled-time" aria-label="Scheduled time" />
            <p className="text-[11px] text-muted-foreground -mt-1" data-testid="med-time-helper">Actual administration date and time are recorded automatically when you submit.</p>
            <Input placeholder="Dose given (optional)" value={form.dose_given} onChange={(e) => setForm({ ...form, dose_given: e.target.value })} data-testid="med-dose" aria-label="Dose given" />
            <Textarea rows={3} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="med-notes" aria-label="Notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="med-administer-submit">{busy ? 'Saving…' : 'Record'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabShell>
    </>
  );
}

export function ChartsTab({ pid }) {
  const { hasPermission } = useAuth();
  const { data, error, denied, loading, reload } = useFetch(`/participants/${pid}/charts`);
  const charts = Array.isArray(data) ? data : (data?.items || data?.data || []);
  // BS3-011: Record opens the same entry forms as the admin chart page (bowel / sleep / template-driven charts)
  const [recordChart, setRecordChart] = useState(null);
  const [recordTemplate, setRecordTemplate] = useState(null);   // template for template-driven charts (null while loading)
  const canRecord = typeof hasPermission === 'function' && hasPermission('participant_charts.record');
  // BS3-011: workers can start a chart themselves (BGL, blood pressure, weight, bowel …), as in the mobile app
  const canCreate = typeof hasPermission === 'function' && hasPermission('participant_charts.create');
  const [addOpen, setAddOpen] = useState(false);   // BS3-011: same Create chart dialog as the admin participant page
  const tplOf = (c) => (c?.template_code || c?.chart_type || '').toLowerCase();
  const isBowel = (c) => tplOf(c) === 'bowel_chart';
  const isSleep = (c) => tplOf(c) === 'sleep_chart';
  const openDialog = (c) => {
    setRecordTemplate(null);
    setRecordChart(c);
    if (!isBowel(c) && !isSleep(c)) {
      getChartTemplate(tplOf(c)).then(setRecordTemplate).catch(() => setRecordTemplate(null));
    }
  };
  const recordProps = recordChart && {
    open: true, participantId: pid, chartId: recordChart.id, onSaved: reload,
    onOpenChange: (o) => { if (!o) setRecordChart(null); },
  };
  return (
    <TabShell loading={loading} error={error} denied={denied} empty={charts.length === 0 && !canCreate} emptyLabel="No clinical charts configured." onRetry={reload} testId="tab-charts">
      <div data-testid="tab-charts">
        {canCreate && (
          <div className="flex justify-end pb-2">
            <Button size="sm" className="h-8" onClick={() => setAddOpen(true)} data-testid="chart-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add chart</Button>
          </div>
        )}
        {charts.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tab-charts-empty">No clinical charts yet — add one to start recording.</div>
        )}
        {charts.slice(0, 25).map((c) => (
          <div key={c.id} className="py-2.5 border-b last:border-0 flex items-center gap-2" data-testid={`chart-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{c.name || c.chart_type}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.chart_type || ''} {c.entry_count != null ? `· ${c.entry_count} entries` : ''}</div>
            </div>
            <Badge variant="outline">{c.status || 'ACTIVE'}</Badge>
            {canRecord && String(c.status || 'ACTIVE').toUpperCase() === 'ACTIVE' && (
              <Button size="sm" variant="outline" className="h-7" onClick={() => openDialog(c)} data-testid={`chart-record-${c.id}`}>
                <Plus className="h-3.5 w-3.5 mr-1" />Record
              </Button>
            )}
          </div>
        ))}
      </div>
      {recordChart && isBowel(recordChart) && <BowelChartEntryFormModal {...recordProps} />}
      {recordChart && isSleep(recordChart) && <SleepChartEntryFormModal {...recordProps} />}
      {recordChart && !isBowel(recordChart) && !isSleep(recordChart) && <ChartDynamicEntryForm {...recordProps} template={recordTemplate} />}
      {/* BS3-011: the admin Create chart dialog (template, chart name, monitoring frequency, notes) */}
      <ParticipantChartCreateModal open={addOpen} onOpenChange={setAddOpen} participantId={pid} onCreated={reload} />
    </TabShell>
  );
}

function DocumentsTab({ pid }) {
  const { data, error, denied, loading, reload } = useFetch(`/participants/${pid}/documents`);
  const items = data?.items || data?.data || (Array.isArray(data) ? data : []);
  const open = async (doc) => {
    try {
      const r = await api.get(`/participants/${pid}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
    } catch { toast.error('Unable to open document'); }
  };
  return (
    <TabShell loading={loading} error={error} denied={denied} empty={items.length === 0} emptyLabel="No documents available." onRetry={reload} testId="tab-documents">
      <div>
        {items.slice(0, 25).map((d) => (
          <Row key={d.id} testId={`doc-${d.id}`} title={d.title || d.file_name || 'Document'}
               meta={`${d.category || ''} ${d.expiry_date ? `· expires ${d.expiry_date}` : ''}`}
               badge={<Button variant="outline" size="sm" className="h-7" onClick={() => open(d)} data-testid={`doc-open-${d.id}`}><FileText className="h-3.5 w-3.5 mr-1" />View</Button>} />
        ))}
      </div>
    </TabShell>
  );
}

function VehicleTab({ pid }) {
  const { data, error, denied, loading, reload } = useFetch(`/staff/participants/${pid}/vehicles`);
  const items = data?.items || [];
  if (!loading && !denied && error) {
    return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tab-vehicle-unavailable">The Available Vehicle view is not enabled yet.</div>;
  }
  return (
    <TabShell loading={loading} error={null} denied={denied} empty={items.length === 0} emptyLabel="No vehicles linked to this participant's house or scope." onRetry={reload} testId="tab-vehicle">
      <div>
        {data?.reason && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">{data.reason}</p>}
        {items.map((v) => (
          <Row key={v.id} testId={`vehicle-${v.id}`} title={`${v.name} — ${v.registration}`}
               meta={`${v.model || ''} ${v.accessibility_type ? `· ${v.accessibility_type}` : ''} ${v.service_due_date ? `· service due ${v.service_due_date}` : ''}`}
               badge={<Badge variant={v.status === 'AVAILABLE' ? 'secondary' : 'outline'} className="gap-1"><Car className="h-3 w-3" />{v.status}</Badge>} />
        ))}
      </div>
    </TabShell>
  );
}

function ParticipantTasksTab({ pid, canCreate }) {
  const { data, error, denied, loading, reload } = useFetch('/participant-tasks', { participant_id: pid, page_size: 50 });
  const items = data?.items || [];
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', priority: 'MEDIUM', due_date: '', assignee_staff_id: '' });
  useEffect(() => {
    if (!open) return;
    api.get('/participant-tasks/assignees', { params: { participant_id: pid } })
      .then((r) => setAssignees(r.data.data || [])).catch(() => setAssignees([]));
  }, [open, pid]);

  const create = async () => {
    if (!form.title.trim()) { toast.error('A task title is required'); return; }
    setBusy(true);
    try {
      await api.post('/participant-tasks', {
        participant_id: pid, title: form.title, description: form.description || undefined,
        priority: form.priority, due_date: form.due_date || undefined,
        assignee_staff_id: form.assignee_staff_id || undefined,
      });
      toast.success('Task created');
      setOpen(false);
      setForm({ title: '', description: '', priority: 'MEDIUM', due_date: '', assignee_staff_id: '' });
      reload();
      window.dispatchEvent(new Event('staff-v2-tasks-changed'));
    } catch (e) { toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Unable to create task'); }
    finally { setBusy(false); }
  };
  const act = async (t, action) => {
    setBusy(true);
    try {
      await api.post(`/participant-tasks/${t.id}/${action}`);
      toast.success(action === 'complete' ? 'Task completed' : 'Task started');
      reload();
      window.dispatchEvent(new Event('staff-v2-tasks-changed'));
    } catch (e) { toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (!loading && !denied && error) {
    return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tab-tasks-unavailable">Participant tasks are not enabled for your organisation yet.</div>;
  }
  return (
    <div className="space-y-3" data-testid="tab-tasks">
      {canCreate && (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="task-create-btn"><Plus className="h-4 w-4 mr-1.5" />New task</Button>
      )}
      {loading ? <div className="py-8 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>
        : denied ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tab-tasks-denied">You don&apos;t have permission to view tasks for this participant.</div>
          : items.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tab-tasks-empty">No tasks recorded for this participant.</div>
            : items.map((t) => <TaskRow key={t.id} t={t} onAction={act} busy={busy} />)}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-create-dialog">
          <DialogHeader><DialogTitle>New participant task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Task title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-form-title" aria-label="Task title" />
            <Textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-description" aria-label="Task description" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs text-muted-foreground space-y-1 block">Priority
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-form-priority" aria-label="Priority">
                  <option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option>
                </select>
              </label>
              <label className="text-xs text-muted-foreground space-y-1 block">Due date
                <TrinitiDatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="task-form-due-date" />
              </label>
            </div>
            <label className="text-xs text-muted-foreground space-y-1 block">Assign to
              <SearchSelect
                options={assignees.map((a) => ({ value: a.id, label: `${a.full_name}${a.role_name ? ` (${roleLabel(a.role_name)})` : ''}` }))}
                value={form.assignee_staff_id}
                onChange={(v) => setForm({ ...form, assignee_staff_id: v })}
                placeholder="Unassigned"
                searchPlaceholder={STAFF_SEARCH_HINT}
                emptyText="No staff match that search"
                clearLabel="Unassigned"
                className="h-9 text-sm"
                testId="task-form-assignee"
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="task-form-cancel">Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="task-form-submit">Create task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function StaffParticipantProfilePage() {
  const { participantId: pid } = useParams();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') || 'careplans';
  const { data: ov, error, denied, loading, reload } = useFetch(`/staff/participants/${pid}/overview`);
  const flags = useStaffV2Flags();
  const { hasPermission } = useAuth();
  const tasksEnabled = !!flags?.participant_tasks_enabled;
  const groups = tasksEnabled
    ? GROUPS.map((g) => (g.title === 'Notes & Records' ? { ...g, tabs: [...g.tabs, ['tasks', 'Tasks']] } : g))
    : GROUPS;

  const setTab = (t) => setSp({ tab: t }, { replace: true });
  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  const tabContent = {
    careplans: <CarePlansTab pid={pid} />,
    goals: <GoalsTab pid={pid} />,
    charts: <ChartsTab pid={pid} />,
    medication: <MedicationTab pid={pid} />,
    risk: <ListTab url={`/risk/by-participant/${pid}`} emptyLabel="No risk assessments on record." testId="tab-risk"
      mapRow={(r) => <Row key={r.id} testId={`risk-${r.id}`} title={r.title || r.risk_type || 'Risk assessment'} meta={`${r.review_date ? `Review ${r.review_date}` : ''}`} badge={<Badge variant="outline">{r.status || '—'}</Badge>} />} />,
    bsp: <div className="py-8 text-center text-sm text-muted-foreground" data-testid="tab-bsp-unavailable">Behaviour Support Plans are not yet available in this portal. Speak to your coordinator for the current plan.</div>,
    notes: <NotesTab pid={pid} />,
    handover: <HandoverTab pid={pid} />,
    documents: <DocumentsTab pid={pid} />,
    appointments: <ListTab url="/appointments" params={{ participant_id: pid }} emptyLabel="No appointments." testId="tab-appointments"
      mapRow={(a) => <Row key={a.id} testId={`appt-${a.id}`} title={a.title || 'Appointment'} meta={`${a.date || ''} ${a.start_time || a.time || ''} ${a.location ? `· ${a.location}` : ''} ${(a.amendments || []).length ? `· ${a.amendments.length} amendment(s)` : ''}`} badge={<Badge variant="outline">{a.status || '—'}</Badge>} />} />,
    incidents: (
      <div className="space-y-3">
        <Button asChild size="sm" data-testid="incident-report-btn">
          <Link to={`/compliance/incidents/new?participant_id=${pid}`}><Plus className="h-4 w-4 mr-1.5" />Report incident</Link>
        </Button>
        <ListTab url="/incidents" params={{ participant_id: pid }} emptyLabel="No incidents recorded." testId="tab-incidents"
          mapRow={(i) => <Row key={i.id} testId={`incident-${i.id}`} title={i.title || i.incident_type || 'Incident'} meta={`${i.incident_type || ''} ${i.reported_at ? `· ${new Date(i.reported_at).toLocaleDateString()}` : ''} ${i.ndis_reportable ? '· NDIS reportable' : ''}`} badge={<Badge variant="outline">{i.status || '—'}</Badge>} />} />
      </div>
    ),
    vehicle: <VehicleTab pid={pid} />,
  };
  if (tasksEnabled) {
    tabContent.tasks = <ParticipantTasksTab pid={pid} canCreate={typeof hasPermission === 'function' && hasPermission('participant_tasks.create')} />;
  }

  if (denied) {
    return (
      <Card data-testid="profile-denied"><CardContent className="py-12 text-center space-y-3">
        <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
        <p className="text-sm text-muted-foreground">You can only view participants assigned to your shifts.</p>
        <Button asChild variant="outline" size="sm"><Link to="/my/work"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to My Shifts</Link></Button>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="staff-participant-profile">
      <Button asChild variant="ghost" size="sm" className="-ml-2" data-testid="profile-back">
        <Link to="/my/work"><ArrowLeft className="h-4 w-4 mr-1.5" />My Shifts</Link>
      </Button>

      {/* Header */}
      <Card data-testid="profile-header">
        <CardContent className="py-4">
          {loading ? <div className="h-16 animate-pulse bg-muted rounded" /> : error ? (
            <div className="text-center space-y-2 py-2"><p className="text-sm text-muted-foreground">{error}</p><Button size="sm" variant="outline" onClick={reload}>Retry</Button></div>
          ) : ov && (
            <div className="flex flex-wrap items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-semibold" aria-hidden="true">
                {(ov.first_name || ' ')[0]}{(ov.last_name || ' ')[0]}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold" data-testid="profile-name">{ov.first_name} {ov.last_name}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
                  <span>Status: {ov.status || '—'}</span>
                  <span>NDIS plan ends: {ov.ndis_plan_end_date || '—'}</span>
                  <span>Coordinator: {ov.support_coordinator || '—'}</span>
                  <span>Last note: {fmt(ov.last_note_at)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {ov.unacknowledged_handovers > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="profile-handover-reminder">{ov.unacknowledged_handovers} unread handover{ov.unacknowledged_handovers > 1 ? 's' : ''}</Badge>
                )}
                {(ov.clinical_alerts || []).length > 0 && (
                  <Badge variant="destructive" className="gap-1" data-testid="profile-clinical-alerts"><AlertTriangle className="h-3 w-3" />{ov.clinical_alerts.length} alert{ov.clinical_alerts.length > 1 ? 's' : ''}</Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grouped tab nav */}
      <nav aria-label="Participant sections" className="space-y-2" data-testid="profile-tab-groups">
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{g.title}</div>
              <div className="flex flex-wrap gap-1">
                {g.tabs.map(([key, label]) => (
                  <button key={key} onClick={() => setTab(key)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${tab === key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                          aria-current={tab === key ? 'page' : undefined}
                          data-testid={`profile-tab-${key}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      <Card><CardContent className="py-4" data-testid="profile-tab-content">{tabContent[tab] || tabContent.careplans}</CardContent></Card>
    </div>
  );
}
